/**
 * Offline mode is gated by TWO things:
 *  1. The BUILD flag `REACT_APP_OFFLINE_MODE` — "is offline AVAILABLE in this
 *     build" (inlined at build time; set on for prod so users CAN opt in).
 *  2. A per-DEVICE user opt-in (localStorage) — the actual activation. Offline
 *     is per-device by nature (the replica lives on the device a vet takes into
 *     the field), so opt-in is stored per device, toggled from Settings.
 *
 * `OFFLINE_ENABLED` is the EFFECTIVE gate everything else reads (provider mount,
 * SW app-shell caching, auth grace, write queue, …). With the build flag on but
 * no opt-in it is `false`, so a non-opted-in user is byte-identical to the
 * pre-offline app. Read once at module load — toggling the opt-in reloads the
 * app so this recomputes.
 */
const OPT_IN_KEY = 'st.offline.optIn';

/** Whether offline CAN be opted into in this build (drives the Settings toggle
 *  visibility) — independent of the current opt-in state. */
export const OFFLINE_AVAILABLE = process.env.REACT_APP_OFFLINE_MODE === 'true';

/** Per-device opt-in read (live; reads localStorage each call). */
export function isOfflineOptIn(): boolean {
  try {
    return localStorage.getItem(OPT_IN_KEY) === 'true';
  } catch {
    return false;
  }
}

/** Persist the per-device opt-in. Callers should reload so OFFLINE_ENABLED
 *  (and the service-worker registration URL) recompute. */
export function setOfflineOptIn(enabled: boolean): void {
  try {
    localStorage.setItem(OPT_IN_KEY, enabled ? 'true' : 'false');
  } catch {
    /* localStorage unavailable — offline simply stays off */
  }
}

/** Pure combiner (build-available AND device-opted-in), for testability. */
export function computeOfflineEnabled(available: boolean, optedIn: boolean): boolean {
  return available && optedIn;
}

/** True when offline mode is enabled FOR THIS DEVICE. Off => the whole feature
 *  is inert (byte-identical to the pre-offline app). Frozen at module load. */
export const OFFLINE_ENABLED = computeOfflineEnabled(OFFLINE_AVAILABLE, isOfflineOptIn());

/**
 * The synced entities replicated locally. `pullKey` = the array key in the
 * `GET /sync/changes` response; `entityType` = the singular string used in the
 * `deletions` feed and `/sync/push`; `table` = the Dexie table name; `byHorse`
 * marks tables that carry a horseId (indexed for chart reads). `clinic` is
 * handled separately (a single object, not an array). Keep this in lockstep
 * with the backend `SYNCED_ENTITY_TYPES` (common/sync-tombstones.ts).
 */
export interface EntityDef {
  pullKey: string;
  entityType: string;
  table: string;
  byHorse: boolean;
  /** The permission the server requires to pull this entity (backend SYNC_ENTITY_PERMISSIONS); none = ungated. */
  view?: string;
}

export const ENTITIES: EntityDef[] = [
  { pullKey: 'horses', entityType: 'horse', table: 'horses', byHorse: false, view: 'view:patients' },
  { pullKey: 'clients', entityType: 'client', table: 'clients', byHorse: false, view: 'view:clients' },
  { pullKey: 'users', entityType: 'user', table: 'users', byHorse: false },
  { pullKey: 'soapTemplates', entityType: 'soapTemplate', table: 'soapTemplates', byHorse: false, view: 'view:medical_records' },
  { pullKey: 'inventoryItems', entityType: 'inventoryItem', table: 'inventoryItems', byHorse: false, view: 'view:inventory' },
  { pullKey: 'appointments', entityType: 'appointment', table: 'appointments', byHorse: true, view: 'view:appointments' },
  { pullKey: 'soapNotes', entityType: 'soapNote', table: 'soapNotes', byHorse: true, view: 'view:medical_records' },
  { pullKey: 'medicalRecords', entityType: 'medicalRecord', table: 'medicalRecords', byHorse: true, view: 'view:medical_records' },
  { pullKey: 'medications', entityType: 'medication', table: 'medications', byHorse: true, view: 'view:medical_records' },
  { pullKey: 'vaccinations', entityType: 'vaccination', table: 'vaccinations', byHorse: true, view: 'view:medical_records' },
  { pullKey: 'procedures', entityType: 'procedure', table: 'procedures', byHorse: true, view: 'view:medical_records' },
  { pullKey: 'dischargeSummaries', entityType: 'dischargeSummary', table: 'dischargeSummaries', byHorse: true, view: 'view:medical_records' },
  { pullKey: 'invoices', entityType: 'invoice', table: 'invoices', byHorse: true, view: 'view:billing' },
  { pullKey: 'clientCommunications', entityType: 'clientCommunication', table: 'clientCommunications', byHorse: true, view: 'view:medical_records' },
  // Bucket C clinical chart entities (offline coverage expansion).
  { pullKey: 'labResults', entityType: 'labResult', table: 'labResults', byHorse: true, view: 'view:medical_records' },
  { pullKey: 'problemLists', entityType: 'problemList', table: 'problemLists', byHorse: true, view: 'view:medical_records' },
  { pullKey: 'recallReminders', entityType: 'recallReminder', table: 'recallReminders', byHorse: true, view: 'view:medical_records' },
  // Client-scoped notes + vet calendar blocks (not horse-scoped → filtered
  // in-memory by the readers; both are low-volume per clinic).
  { pullKey: 'clientNotes', entityType: 'clientNote', table: 'clientNotes', byHorse: false, view: 'view:clients' },
  { pullKey: 'timeBlocks', entityType: 'timeBlock', table: 'timeBlocks', byHorse: false, view: 'view:appointments' },
  // Offline billing: payments (cash collected in the field + balance display)
  // and the service catalog (custom prices in pickers/line items offline).
  { pullKey: 'payments', entityType: 'payment', table: 'payments', byHorse: false, view: 'view:billing' },
  { pullKey: 'serviceCatalog', entityType: 'serviceCatalog', table: 'serviceCatalog', byHorse: false, view: 'view:billing' },
  // Trucks (Phase 3): StockLocation is pull-only reference data (warehouse +
  // truck definitions) — no push case, mirrors serviceCatalog's wiring.
  { pullKey: 'stockLocations', entityType: 'stockLocation', table: 'stockLocations', byHorse: false, view: 'view:inventory' },
];

/** Maps a deletion's singular entityType -> the Dexie table to delete from. */
export const TABLE_BY_ENTITY_TYPE: Record<string, string> = Object.fromEntries(
  ENTITIES.map((e) => [e.entityType, e.table]),
);

/** Maps a pull response array key -> the Dexie table to upsert into. */
export const TABLE_BY_PULL_KEY: Record<string, string> = Object.fromEntries(
  ENTITIES.map((e) => [e.pullKey, e.table]),
);
